import { supabase } from '@/integrations/supabase/client';

/** Languages the active speech provider covers for this tenant. Empty when cloud voice is off, not configured, or the check fails. */
export async function checkCloudVoice(): Promise<string[]> {
  try {
    const { data, error } = await supabase.functions.invoke('synthesize-speech', { body: { check: true } });
    if (error || !data || data.success !== true || data.enabled !== true || data.configured !== true || !Array.isArray(data.languages)) {
      return [];
    }
    return data.languages.filter((l: unknown): l is string => typeof l === 'string');
  } catch {
    return [];
  }
}

/** One chunk of audio from the synthesize-speech function. Plain fetch: supabase-js does not return audio bodies as Blobs. */
export async function fetchSpeechAudio(text: string, language: string, signal: AbortSignal): Promise<Blob> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('not signed in');
  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/synthesize-speech`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ text, language }),
    signal,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  if (!(res.headers.get('Content-Type') ?? '').startsWith('audio/')) throw new Error('response is not audio');
  return res.blob();
}

/** Plays a blob. Resolves when it ends or when `ref.current.cancel()` runs; rejects on a decode error or autoplay rejection. */
export function playBlob(blob: Blob, ref: { current: { cancel: () => void } | null }): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    let settled = false;
    const handle = {
      cancel: () => {
        try { audio.pause(); } catch { /* not playing */ }
        settle(resolve);
      },
    };
    function settle(fn: () => void) {
      if (settled) return;
      settled = true;
      audio.onended = null;
      audio.onerror = null;
      URL.revokeObjectURL(url);
      if (ref.current === handle) ref.current = null;
      fn();
    }
    ref.current = handle;
    audio.onended = () => settle(resolve);
    audio.onerror = () => settle(() => reject(new Error('audio error')));
    audio.play().catch((e) => settle(() => reject(e)));
  });
}
